import jsPDF from 'jspdf';
import 'jspdf-autotable';
import {
  Chart,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip as ChartTooltip,
  Legend as ChartLegend,
} from 'chart.js';

import { generarImagenGraficaCleaver, ANCHO_GRAFICA, ALTO_GRAFICA } from '../utils/graficaCleaver';

Chart.register(RadialLinearScale, PointElement, LineElement, Filler, ChartTooltip, ChartLegend);

// ============================================
// Paleta de marca (teal PsyTest)
// ============================================
const C = {
  primario: [4, 131, 119],      // #048377
  primarioOscuro: [3, 107, 97], // #036b61
  menta: [221, 248, 239],       // #ddf8ef
  texto: [45, 55, 72],          // #2d3748
  textoClaro: [113, 128, 150],  // #718096
  blanco: [255, 255, 255],
  alto: [39, 174, 96],
  medio: [230, 158, 15],
  bajo: [214, 62, 47],
};

const clasificacionColor = (clasificacion) => {
  const s = (clasificacion || '').toString().toLowerCase();
  if (['alto', 'superior', 'sobresaliente', 'término medio alto', 'termino medio alto'].some(t => s.includes(t))) return C.alto;
  if (['bajo', 'inferior', 'deficiente'].some(t => s.includes(t))) return C.bajo;
  return C.medio;
};

let logoCache = null;
const cargarLogoBase64 = async () => {
  if (logoCache !== undefined && logoCache !== null) return logoCache;
  try {
    const response = await fetch('/isotipotransparente.png');
    const blob = await response.blob();
    logoCache = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
    return logoCache;
  } catch (e) {
    console.warn('No se pudo cargar el logo para el PDF:', e);
    return null;
  }
};

// La foto de verificación pasa siempre por nuestro propio backend (nunca es
// una URL de S3 directa), así que el fetch va con la cookie de sesión igual
// que cualquier otra llamada a /api.
const cargarFotoCandidatoBase64 = async (fotoUrl) => {
  if (!fotoUrl) return null;
  try {
    const response = await fetch(fotoUrl, { credentials: 'include' });
    if (!response.ok) return null;
    const blob = await response.blob();
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (e) {
    console.warn('No se pudo cargar la foto del candidato para el PDF:', e);
    return null;
  }
};

// jsPDF's addImage estira la imagen exactamente al ancho/alto que se le dé,
// sin respetar su proporción original, lo que aplastaba fotos de cámara
// (4:3) dentro de la caja vertical del PDF. Aquí se recorta primero en un
// canvas al mismo ratio de la caja (estilo CSS object-fit: cover) para que
// addImage ya no tenga que deformar nada.
const recortarImagenCover = (dataUrl, targetW, targetH) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const escala = Math.max(targetW / img.width, targetH / img.height);
      const srcW = targetW / escala;
      const srcH = targetH / escala;
      const srcX = (img.width - srcW) / 2;
      const srcY = (img.height - srcH) / 2;

      const canvas = document.createElement('canvas');
      canvas.width = targetW;
      canvas.height = targetH;
      canvas.getContext('2d').drawImage(img, srcX, srcY, srcW, srcH, 0, 0, targetW, targetH);
      resolve(canvas.toDataURL('image/jpeg', 0.92));
    };
    img.onerror = reject;
    img.src = dataUrl;
  });

// Dibuja el radar de los 9 rasgos de PERFOP en un canvas desprendido del DOM
// (nunca se inserta en la página) y lo devuelve como PNG base64, listo para
// doc.addImage(). animation:false hace que el render ocurra de forma
// síncrona dentro del constructor, así que toBase64Image() ya tiene algo
// que capturar inmediatamente después.
const generarImagenRadarPerfop = (rasgos) => {
  const lista = Object.values(rasgos || {});
  if (lista.length === 0) return null;

  const tamano = 600;
  const canvas = document.createElement('canvas');
  canvas.width = tamano;
  canvas.height = tamano;

  const chart = new Chart(canvas, {
    type: 'radar',
    data: {
      labels: lista.map(r => r.nombre),
      datasets: [{
        data: lista.map(r => r.decatipo),
        backgroundColor: 'rgba(4, 131, 119, 0.25)',
        borderColor: 'rgba(4, 131, 119, 1)',
        borderWidth: 2,
        pointBackgroundColor: 'rgba(4, 131, 119, 1)',
        pointBorderColor: '#fff',
        pointRadius: 4,
      }],
    },
    options: {
      responsive: false,
      animation: false,
      scales: {
        r: {
          min: 0,
          max: 10,
          ticks: { stepSize: 2, backdropColor: 'transparent', color: '#8a94a6', font: { size: 11 } },
          grid: { color: 'rgba(0, 0, 0, 0.1)' },
          angleLines: { color: 'rgba(0, 0, 0, 0.1)' },
          pointLabels: { color: '#2d3748', font: { size: 13, weight: '600' } },
        },
      },
      plugins: { legend: { display: false }, tooltip: { enabled: false } },
    },
  });

  const imagen = chart.toBase64Image('image/png', 1);
  chart.destroy();
  return imagen;
};

// ============================================
// Helpers de dibujo
// ============================================
const setFill = (doc, rgb) => doc.setFillColor(rgb[0], rgb[1], rgb[2]);
const setText = (doc, rgb) => doc.setTextColor(rgb[0], rgb[1], rgb[2]);
const setDraw = (doc, rgb) => doc.setDrawColor(rgb[0], rgb[1], rgb[2]);

const dibujarPiePagina = (doc, candidatoNombre) => {
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    setDraw(doc, C.menta);
    doc.setLineWidth(0.4);
    doc.line(15, 282, 195, 282);
    doc.setFontSize(8);
    setText(doc, C.textoClaro);
    doc.text(`PsyTest · Reporte confidencial de ${candidatoNombre || 'candidato'}`, 15, 288);
    doc.text(`Página ${i} de ${totalPages}`, 195, 288, { align: 'right' });
  }
};

const encabezadoSeccion = (doc, titulo, y) => {
  setFill(doc, C.primario);
  doc.roundedRect(15, y - 6, 4, 8, 1, 1, 'F');
  doc.setFontSize(14);
  setText(doc, C.texto);
  doc.setFont(undefined, 'bold');
  doc.text(titulo, 23, y);
  doc.setFont(undefined, 'normal');
  return y + 10;
};

// ============================================
// Resumen por tipo de prueba: valor clave + nivel + color
// ============================================
const resumenPrueba = (prueba) => {
  switch (prueba.prueba_clave) {
    case 'TERMAN': {
      const t = prueba.terman;
      if (!t) break;
      return {
        valor: `CI ${t.ci !== null ? t.ci : '< 69'}`,
        nivel: t.rango_ci || 'Sin clasificar',
        detalle: t.rango_capacidad_aprendizaje ? `Capacidad de aprendizaje: ${t.rango_capacidad_aprendizaje}` : '',
        color: clasificacionColor(t.rango_ci),
      };
    }
    case 'OTIS': {
      const o = prueba.otis;
      if (!o) break;
      return {
        valor: `${o.aciertos}/${o.total || prueba.total_preguntas} aciertos`,
        nivel: o.diagnostico || 'Sin clasificar',
        detalle: '',
        color: clasificacionColor(o.diagnostico),
      };
    }
    case 'CLEAVER': {
      const c = prueba.cleaver;
      if (!c || !c.factores) break;
      const entries = Object.entries(c.factores);
      const dominante = entries.reduce((max, cur) => (cur[1].t_escalado > (max?.[1].t_escalado ?? -Infinity) ? cur : max), null);
      if (c.grafica_iii?.patron) {
        return {
          valor: `Patrón ${c.grafica_iii.patron}`,
          nivel: `Segmentos ${c.grafica_iii.codigo} (Gráfica III)`,
          detalle: entries.map(([k, f]) => `${k}: T ${f.t >= 0 ? '+' : ''}${f.t} · segmento ${c.grafica_iii.puntos[k].segmento}`).join('   '),
          color: C.medio,
        };
      }
      return {
        valor: dominante ? `${dominante[1].nombre} (${dominante[0]})` : 'DISC',
        nivel: dominante ? `Rasgo dominante · ${dominante[1].clasificacion}` : 'Cualitativo',
        detalle: entries.map(([k, f]) => `${k}: ${f.t_escalado}`).join(' · '),
        color: dominante ? clasificacionColor(dominante[1].clasificacion) : C.medio,
      };
    }
    case 'PERFOP': {
      const p = prueba.perfop;
      if (!p || !p.compuestos) break;
      const comp = Object.values(p.compuestos);
      return {
        valor: comp.map(c => `${c.nombre.split(' ')[0]}: ${c.decatipo}`).join(' · '),
        nivel: comp.map(c => c.clasificacion).join(' / '),
        detalle: '',
        color: clasificacionColor(comp[0]?.clasificacion),
      };
    }
    case 'PERSONALIZADA': {
      const p = prueba.personalizada;
      if (!p) break;
      if (!p.tiene_calificacion) {
        return {
          valor: 'Encuesta completada',
          nivel: `${prueba.total_respondidas} respuesta${prueba.total_respondidas !== 1 ? 's' : ''} registrada${prueba.total_respondidas !== 1 ? 's' : ''}`,
          detalle: '',
          color: C.medio,
        };
      }
      return {
        valor: `${p.correctas}/${p.total_calificables} (${p.porcentaje}%)`,
        nivel: p.porcentaje >= 75 ? 'Satisfactorio' : p.porcentaje >= 50 ? 'En desarrollo' : 'Área de oportunidad',
        detalle: '',
        color: p.porcentaje >= 75 ? C.alto : p.porcentaje >= 50 ? C.medio : C.bajo,
      };
    }
    default:
      break;
  }

  const total = parseInt(prueba.total_preguntas) || 0;
  const correctas = parseInt(prueba.respuestas_correctas) || 0;
  if (total > 0) {
    const porcentaje = Math.round((correctas / total) * 100);
    return {
      valor: `${correctas}/${total} (${porcentaje}%)`,
      nivel: porcentaje >= 75 ? 'Satisfactorio' : porcentaje >= 50 ? 'En desarrollo' : 'Área de oportunidad',
      detalle: '',
      color: porcentaje >= 75 ? C.alto : porcentaje >= 50 ? C.medio : C.bajo,
    };
  }

  return {
    valor: `${prueba.total_respondidas || 0}/${prueba.total_preguntas || 0} respondidas`,
    nivel: 'Sin datos suficientes',
    detalle: '',
    color: C.textoClaro,
  };
};

// ============================================
// Función principal
// ============================================
// Desarrollo del Terman en el PDF: resumen, capacidades, fortalezas, áreas
// de oportunidad y tabla por serie. Devuelve la nueva posición vertical.
const NIVEL_COLOR_PDF = {
  'Alto': [47, 133, 90],
  'Medio alto': [4, 131, 119],
  'Medio': [183, 121, 31],
  'Bajo': [221, 107, 32],
  'Muy bajo': [197, 48, 48],
};

const detalleTerman = (doc, it, yInicial) => {
  let y = yInicial + 6;
  const salto = (alto) => {
    if (y + alto > 280) {
      doc.addPage();
      y = 20;
    }
  };

  // Resumen
  doc.setFontSize(9.5);
  const lineasResumen = doc.splitTextToSize(it.resumen, 172);
  salto(lineasResumen.length * 4.8 + 8);
  setFill(doc, C.menta);
  doc.roundedRect(15, y - 4, 180, lineasResumen.length * 4.8 + 6, 2, 2, 'F');
  setText(doc, C.texto);
  doc.text(lineasResumen, 19, y + 1);
  y += lineasResumen.length * 4.8 + 8;

  // Capacidades
  doc.autoTable({
    startY: y,
    head: [['Capacidad', 'Series', 'Logro', 'Nivel']],
    body: it.areas.map(a => [a.nombre, a.series.join(', '), `${a.porcentaje}%`, a.nivel]),
    theme: 'grid',
    headStyles: { fillColor: C.primario, textColor: C.blanco, fontStyle: 'bold' },
    styles: { fontSize: 8.5, cellPadding: 2.5 },
    rowPageBreak: 'avoid',
    margin: { left: 15, right: 15, bottom: 20 },
    columnStyles: { 2: { halign: 'center' }, 3: { fontStyle: 'bold' } },
    didParseCell: (data) => {
      if (data.section === 'body' && data.column.index === 3) data.cell.styles.textColor = NIVEL_COLOR_PDF[data.cell.raw] || C.texto;
    },
  });
  y = doc.lastAutoTable.finalY + 8;

  // Fortalezas y áreas de oportunidad
  const bloque = (titulo, items, color, vacio) => {
    salto(14);
    setText(doc, color);
    doc.setFontSize(10.5);
    doc.setFont(undefined, 'bold');
    doc.text(titulo, 15, y);
    doc.setFont(undefined, 'normal');
    y += 6;
    doc.setFontSize(9);
    if (!items.length) {
      setText(doc, C.textoClaro);
      doc.text(vacio, 19, y);
      y += 6;
      return;
    }
    items.forEach((item) => {
      const lineas = doc.splitTextToSize(item.texto, 168);
      salto(5 + lineas.length * 4.3 + 3);
      setText(doc, C.texto);
      doc.setFont(undefined, 'bold');
      doc.text(`• ${item.nombre} (${item.porcentaje}%)`, 17, y);
      doc.setFont(undefined, 'normal');
      y += 4.6;
      setText(doc, C.textoClaro);
      doc.text(lineas, 21, y);
      y += lineas.length * 4.3 + 3;
    });
    y += 2;
  };
  bloque(`Fortalezas${it.fortalezas_relativas ? ' (resultados relativos)' : ''}`, it.fortalezas, NIVEL_COLOR_PDF.Alto, 'Sin fortalezas marcadas.');
  bloque(`Áreas de oportunidad${it.areas_oportunidad_relativas ? ' (resultados relativos)' : ''}`, it.areas_oportunidad, NIVEL_COLOR_PDF.Bajo, 'No presenta áreas de oportunidad marcadas.');

  // Detalle por serie
  salto(30);
  doc.autoTable({
    startY: y,
    head: [['Serie', 'Qué mide', 'Puntaje', 'Logro', 'Nivel']],
    body: it.series.map(s => [`${s.serie} · ${s.nombre}`, s.mide, `${s.puntaje} / ${s.maximo}`, `${s.porcentaje}%`, s.nivel]),
    theme: 'striped',
    headStyles: { fillColor: C.primario, textColor: C.blanco, fontStyle: 'bold' },
    alternateRowStyles: { fillColor: [245, 249, 248] },
    styles: { fontSize: 8, cellPadding: 2.2, valign: 'middle' },
    rowPageBreak: 'avoid',
    margin: { left: 15, right: 15, bottom: 20 },
    columnStyles: { 0: { cellWidth: 32, fontStyle: 'bold' }, 1: { cellWidth: 82 }, 2: { halign: 'center' }, 3: { halign: 'center' }, 4: { fontStyle: 'bold' } },
    didParseCell: (data) => {
      if (data.section === 'body' && data.column.index === 4) data.cell.styles.textColor = NIVEL_COLOR_PDF[data.cell.raw] || C.texto;
    },
  });
  y = doc.lastAutoTable.finalY + 4;
  doc.setFontSize(7.5);
  setText(doc, C.textoClaro);
  const nota = doc.splitTextToSize(it.nota, 180);
  salto(nota.length * 3.5 + 2);
  doc.text(nota, 15, y + 2);
  return y + nota.length * 3.5 + 2;
};

export const generarReportePDF = async (data, candidato) => {
  const doc = new jsPDF('p', 'mm', 'a4');
  const { resultados: pruebas = [], estadisticas_globales = {}, stack, integridad, foto_url } = data;
  const logo = await cargarLogoBase64();
  let fotoCandidato = await cargarFotoCandidatoBase64(foto_url);
  const nombreCandidato = candidato?.nombre_completo || 'Candidato';

  // ==================== PORTADA ====================
  setFill(doc, C.primario);
  doc.rect(0, 0, 210, 42, 'F');

  if (logo) {
    try {
      setFill(doc, C.blanco);
      doc.circle(28, 21, 12, 'F');
      doc.addImage(logo, 'PNG', 18, 11, 20, 20);
    } catch (e) { /* logo opcional */ }
  }

  doc.setFontSize(24);
  setText(doc, C.blanco);
  doc.setFont(undefined, 'bold');
  doc.text('PsyTest', 48, 20);
  doc.setFont(undefined, 'normal');
  doc.setFontSize(11);
  doc.text('Reporte de Evaluación Psicométrica', 48, 28);
  doc.setFontSize(8);
  setText(doc, C.menta);
  doc.text('Documento confidencial · Uso exclusivo para proceso de selección', 48, 34);

  if (fotoCandidato) {
    try {
      const boxX = 172, boxY = 48, boxW = 23, boxH = 28;
      const fotoW = boxW - 2, fotoH = boxH - 2;
      // Recorte "cover" al ratio exacto de la caja (x10 para buena nitidez)
      // así addImage ya no tiene que estirar/deformar la foto original.
      const fotoRecortada = await recortarImagenCover(fotoCandidato, fotoW * 10, fotoH * 10);
      setFill(doc, C.blanco);
      setDraw(doc, C.primario);
      doc.roundedRect(boxX, boxY, boxW, boxH, 2, 2, 'FD');
      doc.addImage(fotoRecortada, 'JPEG', boxX + 1, boxY + 1, fotoW, fotoH);
    } catch (e) { /* foto opcional */ }
  }

  let y = 56;
  doc.setFontSize(18);
  setText(doc, C.texto);
  doc.setFont(undefined, 'bold');
  doc.text(nombreCandidato, 15, y, { maxWidth: fotoCandidato ? 150 : 180 });
  doc.setFont(undefined, 'normal');

  y += 8;
  doc.setFontSize(10);
  setText(doc, C.textoClaro);
  const infoLinea = [
    candidato?.email,
    candidato?.empresa,
    stack ? `Stack: ${stack}` : null,
    `Fecha: ${new Date().toLocaleDateString('es-MX')}`,
  ].filter(Boolean).join('   ·   ');
  doc.text(doc.splitTextToSize(infoLinea, fotoCandidato ? 150 : 180), 15, y);

  y += 12;
  if (fotoCandidato) y = Math.max(y, 82);
  y = encabezadoSeccion(doc, 'Hallazgos Relevantes', y);

  const highlights = pruebas
    .filter(p => p.total_respondidas > 0)
    .map(p => ({ prueba: p.prueba_nombre, ...resumenPrueba(p) }));

  if (highlights.length === 0) {
    doc.setFontSize(10);
    setText(doc, C.textoClaro);
    doc.text('El candidato aún no cuenta con pruebas respondidas.', 15, y);
    y += 10;
  } else {
    const colW = 90;
    highlights.forEach((h, idx) => {
      const col = idx % 2;
      const row = Math.floor(idx / 2);
      const x = 15 + col * (colW + 5);
      const boxY = y + row * 30;

      setFill(doc, [248, 250, 249]);
      setDraw(doc, [230, 235, 238]);
      doc.roundedRect(x, boxY, colW, 25, 3, 3, 'FD');

      setFill(doc, h.color);
      doc.roundedRect(x, boxY, 3, 25, 1.5, 1.5, 'F');

      doc.setFontSize(9);
      setText(doc, C.textoClaro);
      doc.text(h.prueba, x + 7, boxY + 7);

      doc.setFontSize(11);
      setText(doc, C.texto);
      doc.setFont(undefined, 'bold');
      const valorLines = doc.splitTextToSize(h.valor, colW - 10);
      doc.text(valorLines.slice(0, 2), x + 7, boxY + 14);
      doc.setFont(undefined, 'normal');

      doc.setFontSize(8.5);
      setText(doc, h.color);
      doc.text(h.nivel, x + 7, boxY + 21);
    });
    y += Math.ceil(highlights.length / 2) * 30 + 8;
  }

  // ==================== AVISO DE INTEGRIDAD ====================
  if (integridad?.riesgo_fraude) {
    const esRojo = integridad.riesgo_fraude === 'rojo';
    const colorFondo = esRojo ? [255, 245, 245] : [255, 250, 240];
    const colorBorde = esRojo ? [254, 178, 178] : [251, 211, 141];
    const colorTexto = esRojo ? [130, 39, 39] : [146, 64, 14];

    const mensaje = `Se detectaron ${integridad.cambios_pestana} cambio${integridad.cambios_pestana !== 1 ? 's' : ''} de pestaña` +
      (integridad.respuestas_rapidas > 0
        ? ` y ${integridad.respuestas_rapidas} respuesta${integridad.respuestas_rapidas !== 1 ? 's' : ''} contestada${integridad.respuestas_rapidas !== 1 ? 's' : ''} en menos de 2 segundos`
        : '') +
      ' durante las pruebas de habilidad (OTIS/Terman-Merrill). Se recomienda considerar esta señal como parte de la evaluación del candidato.';
    const lineasAviso = doc.splitTextToSize(mensaje, 165);
    const alturaCaja = 15 + lineasAviso.length * 5;

    setFill(doc, colorFondo);
    setDraw(doc, colorBorde);
    doc.roundedRect(15, y, 180, alturaCaja, 3, 3, 'FD');
    setFill(doc, colorTexto);
    doc.roundedRect(15, y, 3, alturaCaja, 1.5, 1.5, 'F');

    doc.setFontSize(10.5);
    setText(doc, colorTexto);
    doc.setFont(undefined, 'bold');
    doc.text(esRojo ? 'MUY PROBABLE INDICIO DE FRAUDE EN SUS RESPUESTAS' : 'PROBABLE INDICIO DE FRAUDE EN SUS RESPUESTAS', 22, y + 8);
    doc.setFont(undefined, 'normal');
    doc.setFontSize(9);
    doc.text(lineasAviso, 22, y + 15);

    y += alturaCaja + 8;
  }

  y = encabezadoSeccion(doc, 'Resumen Ejecutivo', y);
  doc.setFontSize(10);
  setText(doc, C.textoClaro);
  const nivelGeneral = estadisticas_globales.porcentaje_global >= 75 ? 'satisfactorio' :
    estadisticas_globales.porcentaje_global >= 50 ? 'en desarrollo' : 'con áreas de oportunidad';
  const resumenTexto = `El candidato completó ${estadisticas_globales.total_pruebas || pruebas.length} de las pruebas asignadas` +
    (stack ? ` en el stack "${stack}"` : '') + `. ` +
    (estadisticas_globales.total_preguntas > 0
      ? `En las pruebas de habilidad (razonamiento) obtuvo un desempeño ${nivelGeneral}, con ${estadisticas_globales.porcentaje_global}% de respuestas correctas sobre ${estadisticas_globales.total_preguntas} reactivos. `
      : '') +
    `Los hallazgos de personalidad y aptitud se detallan a continuación para apoyar la decisión de contratación.`;
  const resumenLines = doc.splitTextToSize(resumenTexto, 180);
  doc.text(resumenLines, 15, y);

  // ==================== TABLA RESUMEN ====================
  doc.addPage();
  y = 20;
  setFill(doc, C.primario);
  doc.roundedRect(15, y - 6, 4, 8, 1, 1, 'F');
  doc.setFontSize(14);
  setText(doc, C.texto);
  doc.setFont(undefined, 'bold');
  doc.text('Resultados por Prueba', 23, y);
  doc.setFont(undefined, 'normal');

  const tableData = pruebas.map(p => {
    const r = resumenPrueba(p);
    return [p.prueba_nombre, r.valor, r.nivel];
  });

  doc.autoTable({
    startY: y + 8,
    head: [['Prueba', 'Resultado', 'Nivel / Diagnóstico']],
    body: tableData,
    theme: 'striped',
    headStyles: {
      fillColor: C.primario,
      textColor: C.blanco,
      fontStyle: 'bold',
    },
    alternateRowStyles: { fillColor: [245, 249, 248] },
    styles: { fontSize: 9.5, cellPadding: 5 },
  });

  // ==================== DETALLE POR PRUEBA ====================
  let yPos = doc.lastAutoTable.finalY + 16;

  for (const prueba of pruebas) {
    if (prueba.total_respondidas === 0) continue;

    if (yPos > 250) {
      doc.addPage();
      yPos = 20;
    }

    const r = resumenPrueba(prueba);
    setFill(doc, C.primario);
    doc.roundedRect(15, yPos - 6, 4, 8, 1, 1, 'F');
    doc.setFontSize(12);
    setText(doc, C.texto);
    doc.setFont(undefined, 'bold');
    doc.text(prueba.prueba_nombre, 23, yPos);
    doc.setFont(undefined, 'normal');

    yPos += 8;
    doc.setFontSize(10);
    setText(doc, r.color);
    doc.setFont(undefined, 'bold');
    doc.text(`${r.valor}  ·  ${r.nivel}`, 15, yPos);
    doc.setFont(undefined, 'normal');

    if (r.detalle) {
      yPos += 6;
      doc.setFontSize(9);
      setText(doc, C.textoClaro);
      const detalleLines = doc.splitTextToSize(r.detalle, 175);
      doc.text(detalleLines, 15, yPos);
      yPos += detalleLines.length * 4.5;
    }

    if (prueba.prueba_clave === 'TERMAN' && prueba.terman?.interpretacion) {
      yPos = detalleTerman(doc, prueba.terman.interpretacion, yPos);
    }

    if (prueba.prueba_clave === 'CLEAVER' && prueba.cleaver?.grafica_iii?.puntos) {
      const imagenGrafica = generarImagenGraficaCleaver(prueba.cleaver.grafica_iii);
      if (imagenGrafica) {
        const anchoGrafica = 80;
        const altoGrafica = anchoGrafica * (ALTO_GRAFICA / ANCHO_GRAFICA);
        yPos += 6;
        if (yPos + altoGrafica > 280) {
          doc.addPage();
          yPos = 20;
        }
        doc.addImage(imagenGrafica, 'PNG', (210 - anchoGrafica) / 2, yPos, anchoGrafica, altoGrafica);
        yPos += altoGrafica + 4;
      }
    }

    if (prueba.prueba_clave === 'PERFOP' && prueba.perfop?.rasgos) {
      const imagenRadar = generarImagenRadarPerfop(prueba.perfop.rasgos);
      if (imagenRadar) {
        const tamanoRadar = 85;
        yPos += 6;
        if (yPos + tamanoRadar > 275) {
          doc.addPage();
          yPos = 20;
        }
        const xRadar = (210 - tamanoRadar) / 2;
        doc.addImage(imagenRadar, 'PNG', xRadar, yPos, tamanoRadar, tamanoRadar);
        yPos += tamanoRadar + 4;
      }
    }

    yPos += 10;
  }

  // ==================== FORTALEZAS Y ÁREAS DE OPORTUNIDAD ====================
  if (yPos > 230) {
    doc.addPage();
    yPos = 20;
  }
  yPos = encabezadoSeccion(doc, 'Fortalezas y Áreas de Oportunidad', yPos);

  const evaluadas = pruebas.filter(p => p.total_respondidas > 0).map(p => ({ nombre: p.prueba_nombre, ...resumenPrueba(p) }));
  const fortalezas = evaluadas.filter(e => e.color === C.alto);
  const oportunidades = evaluadas.filter(e => e.color === C.bajo);

  doc.setFontSize(10.5);
  setText(doc, C.alto);
  doc.setFont(undefined, 'bold');
  doc.text('Fortalezas detectadas', 15, yPos);
  doc.setFont(undefined, 'normal');
  yPos += 7;
  doc.setFontSize(9.5);
  setText(doc, C.textoClaro);
  if (fortalezas.length > 0) {
    fortalezas.forEach(f => {
      doc.text(`•  ${f.nombre} — ${f.nivel}`, 18, yPos);
      yPos += 6;
    });
  } else {
    doc.text('•  No se identificaron fortalezas destacadas en el nivel más alto.', 18, yPos);
    yPos += 6;
  }

  yPos += 6;
  doc.setFontSize(10.5);
  setText(doc, C.bajo);
  doc.setFont(undefined, 'bold');
  doc.text('Áreas de oportunidad', 15, yPos);
  doc.setFont(undefined, 'normal');
  yPos += 7;
  doc.setFontSize(9.5);
  setText(doc, C.textoClaro);
  if (oportunidades.length > 0) {
    oportunidades.forEach(o => {
      doc.text(`•  ${o.nombre} — ${o.nivel}`, 18, yPos);
      yPos += 6;
    });
  } else {
    doc.text('•  No se identificaron áreas críticas de oportunidad.', 18, yPos);
    yPos += 6;
  }

  yPos += 10;
  if (yPos > 260) {
    doc.addPage();
    yPos = 20;
  }
  doc.setFontSize(10.5);
  setText(doc, C.texto);
  doc.setFont(undefined, 'bold');
  doc.text('Recomendaciones', 15, yPos);
  doc.setFont(undefined, 'normal');
  yPos += 7;
  doc.setFontSize(9.5);
  setText(doc, C.textoClaro);
  const recomendaciones = [
    'Contrastar estos hallazgos con una entrevista estructurada antes de la decisión final.',
    'Programar una sesión de retroalimentación con el equipo de psicología si se requiere mayor profundidad.',
    'Considerar el perfil de personalidad junto con las competencias técnicas del puesto vacante.',
  ];
  recomendaciones.forEach(rec => {
    const lines = doc.splitTextToSize(`•  ${rec}`, 175);
    doc.text(lines, 15, yPos);
    yPos += lines.length * 5.5;
  });

  dibujarPiePagina(doc, nombreCandidato);

  doc.save(`Reporte_${nombreCandidato.replace(/\s/g, '_')}.pdf`);
};

export default generarReportePDF;
